import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Client, Id } from '@hero/schema';
import { useClientListApi, useClientApi } from '@hero/api-hooks';
import { useFeature } from '@hero/web-context';
import { SelectAsync, type SelectionValue, type SelectAsyncMode } from '../../../app/Select';
import { ClientEditModal, ClientEditModalProps } from '../Modal/ClientEditModal';

/** Client selection value: SelectionValue<Client> */
export type ClientSelectionValue = SelectionValue<Client>;

export interface ClientSelectorProps {
  selectedClients?: ClientSelectionValue;
  /** Called when selection changes. */
  onClientsSelected: (clients: Client[] | undefined) => void;
  /** Called when client is edited/created from the modal. */
  onClientSave?: ClientEditModalProps['onClientSaved'];
  allowCreateNew?: boolean;
  disabled?: boolean;
  placeholder?: string;
  selectMode?: SelectAsyncMode;
  /** Label above the select (rendered via InputLabel when provided). */
  label?: string;
  /** Show required indicator on the label. Use with label. */
  required?: boolean;
}

/**
 * `ClientSelector` — pick or create a `Client` (CRM, relations-v2).
 * Replaces `CustomerSelector`.
 */
export const ClientSelector = React.memo(function ClientSelector({
  selectedClients,
  onClientsSelected,
  onClientSave,
  allowCreateNew: allowCreateNewProp,
  disabled = false,
  placeholder,
  selectMode = 'single',
  label,
  required,
}: ClientSelectorProps) {
  const { t } = useTranslation(['m-client', 'c-main']);
  // Feature gate kept under the legacy module key for now; renaming
  // it requires a coordinated change in the feature-flag registry.
  const { isEnabled: canCreateClient } = useFeature('customer_module', 'sub_create_customer');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingClientId, setEditingClientId] = useState<Id | undefined>(undefined);

  const clientListQuery = useClientListApi({});
  const clientSingleQuery = useClientApi(undefined);

  const allowCreateNew = allowCreateNewProp !== false && canCreateClient;

  const openCreateModal = useCallback(() => {
    setIsCreateModalOpen(true);
    setEditingClientId(undefined);
  }, []);

  const closeModal = useCallback(() => {
    setIsCreateModalOpen(false);
    setEditingClientId(undefined);
  }, []);

  const handleEdit = useCallback((client: Client) => {
    setEditingClientId(client._id);
  }, []);

  const handleClientSave = useCallback(
    (data: { client: Client }) => {
      onClientSave?.(data);
      closeModal();
    },
    [onClientSave, closeModal],
  );

  const rolesLabel = (client: Client) =>
    (client.roles ?? [])
      .slice(0, 2)
      .map(role => t(`m-client:roles.${role}.title`))
      .join(', ');

  const isModalOpen = isCreateModalOpen || editingClientId != null;

  return (
    <div className="space-y-3">
      <SelectAsync<Client>
        value={selectedClients}
        listQuery={clientListQuery}
        singleQuery={clientSingleQuery}
        onChangeSelect={onClientsSelected}
        getItemId={c => c._id}
        placeholder={placeholder || t('modal.select_client_title')}
        searchPlaceholder={t('modal.search_client_placeholder')}
        emptyMessage={t('modal.no_clients_message')}
        loadingMessage={t('c-main:async_operations.loading')}
        createNewLabel={t('c-main:crud_buttons.create_new')}
        onCreateNew={openCreateModal}
        allowCreateNew={allowCreateNew}
        allowEdit={false}
        onEdit={handleEdit}
        mode={selectMode}
        disabled={disabled}
        label={label}
        required={required}
        getOptionDisplay={client => ({
          label: client.name,
          desc: rolesLabel(client),
        })}
        optionDisplayStyle={{ color: 'primary' }}
      />
      {allowCreateNew && (
        <ClientEditModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onClientSaved={handleClientSave}
          clientId={editingClientId}
          allowOutsideClickClose={false}
        />
      )}
    </div>
  );
});
